import React, { useRef } from 'react';

import { Control, useController } from 'react-hook-form';

import { FormControl, FormHelperText, Stack, TextField } from '@mui/material';

import BrandContentSelector from './BrandContentSelector';

type Props = {
  control: Control<any>;
  disabled?: boolean;
  helperText?: string;
  label: string;
  name: string;
  canUseBrandTokens?: boolean;
  error?: boolean;
  required?: boolean;
};

// important: intended to be used inside react hook form
// we should think about replacing this component with MUI RTE when it gets released
// https://aussiecommerce.atlassian.net/browse/ENGX-747
function RichTextEditor({
  control,
  disabled,
  helperText,
  label,
  name,
  canUseBrandTokens,
  error = false,
  required = false,
}: Props) {
  const {
    field: { value, onBlur, onChange },
  } = useController({
    control,
    name,
  });
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const textValue = value ?? '';

  const handleInsertToken = (token: string) => {
    const input = inputRef.current;
    const start = input?.selectionStart ?? textValue.length;
    const end = input?.selectionEnd ?? textValue.length;
    const nextValue = `${textValue.slice(0, start)}${token}${textValue.slice(end)}`;

    onChange(nextValue);

    requestAnimationFrame(() => {
      input?.focus();
      input?.setSelectionRange(start + token.length, start + token.length);
    });
  };

  return (
    <FormControl variant="standard" sx={{ display: 'unset' }} data-testid={`${name}-editor`}>
      <Stack spacing={1}>
        {canUseBrandTokens && <BrandContentSelector onInsertToken={handleInsertToken} disabled={disabled} />}
        <TextField
          label={label}
          required={required}
          value={textValue}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          disabled={disabled}
          error={error}
          multiline
          minRows={6}
          fullWidth
          inputRef={inputRef}
        />
      </Stack>
      <FormHelperText error={error}>{helperText}</FormHelperText>
    </FormControl>
  );
}

export default RichTextEditor;
